import { GlobalStyle } from '@src/styles/GlobalStyle';
import { BaseStyle, shouldForwardStyledProp } from '@talos/kyoko';
import { useLayoutEffect, useState, type ReactNode } from 'react';
import { StyleSheetManager } from 'styled-components';
import { STYLE_TAG_ID } from '../Layout/tokens';
import { copyStaticStyles } from './copyStaticStyles';

interface PopoutStyleProviderProps {
  /**
   * Extra createGlobalStyle output that belongs to a specific popout surface.
   * Example: DockViewStyles must be generated inside Dockview popout documents
   * because the main app styled-components sheet is intentionally not copied.
   */
  additionalGlobalStyles?: ReactNode;
  children: ReactNode;
  includeGlobalStyles?: boolean;
  popoutWindow: Window | null | undefined;
}

interface PopoutDocumentSetup {
  dispose: () => void;
  providerCount: number;
  stylesTarget: HTMLElement;
}

// Dockview renders panel content, tabs, and header actions through separate
// React subtrees. Several PopoutStyleProvider instances can therefore target
// the same popout document. Keep the document-level setup shared so we copy
// static CSS once and install one theme observer per document. WeakMap avoids
// keeping closed popout documents alive.
const activeDocumentSetups = new WeakMap<Document, PopoutDocumentSetup>();

/**
 * Targets styled-components output at a popout document.
 *
 * Popouts are separate documents, so styled-components rules generated by their
 * React subtree must be inserted into the popout window, not the parent app.
 * The document-level setup is de-duped separately because Dockview can mount
 * multiple styled-components contexts inside one popout document.
 */
export function PopoutStyleProvider({
  additionalGlobalStyles,
  children,
  includeGlobalStyles = true,
  popoutWindow,
}: PopoutStyleProviderProps) {
  const popoutDocument = popoutWindow?.document;
  const [stylesTarget, setStylesTarget] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (popoutDocument == null) {
      return;
    }

    // Each provider owns its styled-components context, but all providers for
    // one document share the static CSS copy and theme attribute observer.
    const setup = acquirePopoutDocumentSetup(popoutDocument);
    setStylesTarget(setup.stylesTarget);
    return setup.dispose;
  }, [popoutDocument]);

  if (popoutDocument == null) {
    return <>{children}</>;
  }

  const currentStylesTarget = stylesTarget?.ownerDocument === popoutDocument ? stylesTarget : null;
  if (currentStylesTarget == null) {
    return null;
  }

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardStyledProp} target={currentStylesTarget}>
      <>
        {includeGlobalStyles && (
          <>
            <BaseStyle />
            <GlobalStyle />
          </>
        )}
        {additionalGlobalStyles}
        {children}
      </>
    </StyleSheetManager>
  );
}

/**
 * Due to how DockView works, we have to render several StyleSheetManager providers within one popout document.
 * There is no wrap-all where we can put it. Dockview has 4 "rendering outlets" where we put our components, which each need a parent
 * StyleSheetMananger. Despite having 4 StyleSheetManagers, we want to just do the copy-and-observe work once. To achieve this, we maintain
 * a WeakMap conveying what work has already been done.
 *  */
function acquirePopoutDocumentSetup(popoutDocument: Document) {
  const activeSetup = activeDocumentSetups.get(popoutDocument);
  if (activeSetup != null) {
    // If someone has already setup this Document (reference equality), then just +1 the count and return.
    activeSetup.providerCount += 1;
    return {
      dispose: () => releasePopoutDocumentSetup(popoutDocument),
      stylesTarget: activeSetup.stylesTarget,
    };
  }

  // Else, we are the first ones here, so we should perform the setup.
  const setup = setupPopoutDocument(popoutDocument);
  activeDocumentSetups.set(popoutDocument, {
    dispose: setup.dispose,
    providerCount: 1,
    stylesTarget: setup.stylesTarget,
  });

  return {
    dispose: () => releasePopoutDocumentSetup(popoutDocument),
    stylesTarget: setup.stylesTarget,
  };
}

// This is the cleanup function of our "onMount" useLayoutEffect above, so this will run on "onUnMount" effectively
function releasePopoutDocumentSetup(popoutDocument: Document) {
  const activeSetup = activeDocumentSetups.get(popoutDocument);
  if (activeSetup == null) {
    return;
  }

  activeSetup.providerCount -= 1;
  if (activeSetup.providerCount > 0) {
    return;
  }

  activeSetup.dispose();
  activeDocumentSetups.delete(popoutDocument);
}

function setupPopoutDocument(popoutDocument: Document) {
  // Copy app/library CSS that is not owned by styled-components. Component
  // styles should be generated locally by the StyleSheetManager above; copying
  // parent styled-components sheets can create same-specificity cascade ties.
  copyStaticStyles(popoutDocument);
  const stylesTarget = ensurePopoutStylesTarget(popoutDocument);

  // Apply the current root attributes immediately, then keep them in sync via the observer below from here on out
  syncDocumentElementAttributes(popoutDocument);
  const observer = new MutationObserver(mutations => {
    if (mutations.some(mutation => shouldMirrorDocumentElementAttribute(mutation.attributeName))) {
      syncDocumentElementAttributes(popoutDocument);
    }
  });

  // Helix ThemeProvider mutates the main document root, e.g. toggles `dark`
  // and updates `data-theme`. Popouts do not receive those DOM mutations, but
  // their global styles read the same root attributes.
  observer.observe(document.documentElement, { attributes: true });

  return {
    dispose: () => observer.disconnect(),
    stylesTarget,
  };
}

function ensurePopoutStylesTarget(popoutDocument: Document) {
  let stylesTarget = popoutDocument.getElementById(STYLE_TAG_ID);
  if (stylesTarget == null) {
    stylesTarget = popoutDocument.createElement('div');
    stylesTarget.id = STYLE_TAG_ID;
  }

  // Keep styled-components rules after copied static sheets, matching the main
  // document where static CSS precedes Ava's styled-components target.
  popoutDocument.head.appendChild(stylesTarget);
  return stylesTarget;
}

function syncDocumentElementAttributes(targetDocument: Document) {
  const sourceElement = document.documentElement;
  const targetElement = targetDocument.documentElement;

  targetElement.className = sourceElement.className;

  for (const attribute of Array.from(targetElement.attributes)) {
    if (attribute.name.startsWith('data-') && !sourceElement.hasAttribute(attribute.name)) {
      targetElement.removeAttribute(attribute.name);
    }
  }

  for (const attribute of Array.from(sourceElement.attributes)) {
    if (attribute.name.startsWith('data-')) {
      targetElement.setAttribute(attribute.name, attribute.value);
    }
  }
}

function shouldMirrorDocumentElementAttribute(attributeName: string | null) {
  return attributeName === 'class' || attributeName?.startsWith('data-') === true;
}
